import InfiniteLoader from "$root/components/InfiniteLoader";
import PageLayout from "$root/components/PageLayout";
import { sphereAIAvailable } from "$root/functional-areas/sphere-ai/config";
import { useSystemValue } from "$root/functional-areas/system";
import { useUserValue } from "$root/functional-areas/user";
import { needsMfaEnrollment } from "$root/functional-areas/user/mfa";
import { trackMixPanelEvent } from "$root/third-party-integrations/initMixPanel";
import { Row } from "@mdotm/shadcn/legacy/components";
import { useEventListener } from "@mdotm/shadcn/legacy/react-extensions";
import { useAtom, useAtomValue } from "@mdotm/shadcn/legacy/reactivity";
import { maybeCall } from "@mdotm/shadcn/legacy/utils";
import { lazy, Suspense, useEffect, useLayoutEffect, useMemo, useState } from "react";
import { Redirect, Route, Switch, useHistory } from "react-router-dom";
import { AppDrawer } from "../AppDrawer";
import { SphereAIDockPanel } from "$root/functional-areas/sphere-ai/SphereAIDockPanel";
import GlobalErrorBoundary from "../GlobalErrorBoundary";
import { platformLayoutOptions$ } from "./layout-options";
import { extractPath, routesMap, typedPropsFromUrl, typedUrlForRoute } from "./RoutesDef";

const MfaEnrollment = lazy(() => import("../../pages/MfaEnrollment"));

// Routes that drive the auth handshake itself must never be pre-empted by the
// forced-enrollment screen: `AuthCallback` still has to finish the OIDC exchange,
// and `Login` already redirects a signed-in user onward (where the gate applies).
const MFA_GATE_EXEMPT_ROUTES = new Set<keyof typeof routesMap>([
	"Login",
	"AuthCallback",
	"AuthCallbackForEmailDomain",
	"SsoRedirect",
]);

const PlatformRouter = (): React.JSX.Element => {
	return (
		<GlobalErrorBoundary>
			<PlatformRouterInner />
		</GlobalErrorBoundary>
	);
};

const PlatformRouterInner = (): React.JSX.Element => {
	const { systemMode } = useSystemValue();
	const history = useHistory();

	useEffect(() => {
		trackMixPanelEvent("pageView", {
			pathname: history.location.pathname,
		});
	}, [history.location.pathname]);

	// Intercept regular <a href="...">...</a> when the origin is the same as the window
	useEventListener(window, "click", (e) => {
		const a = e.target instanceof HTMLAnchorElement ? e.target : (e.target as HTMLElement | undefined)?.closest("a");
		if (
			// Not an anchor element
			!a ||
			a.target === "_blank" ||
			// Modifiers or middle/right click
			e.metaKey ||
			e.shiftKey ||
			e.altKey ||
			e.ctrlKey ||
			e.button !== 0 ||
			// Download anchors
			a.getAttribute("download") ||
			// Anchors to different origins
			a.origin !== window.location.origin
		) {
			return;
		}

		e.preventDefault();
		history.push(a.pathname + a.search + a.hash);
	});

	const platformLayoutOptions = useAtomValue(platformLayoutOptions$);
	const user = useUserValue();

	const routes = useMemo(
		() =>
			systemMode === "MAINTENANCE" ? (
				<Route path="*">
					<PageWrapper routeKey="Maintenance" route={routesMap["Maintenance"]} />
				</Route>
			) : (
				Object.entries(routesMap).map(([key, route]) => {
					const routePath = route.baseUrl === "*" ? route.baseUrl : extractPath(route.baseUrl);
					return (
						<Route key={key} exact={route.baseUrl !== "*"} path={routePath}>
							<PageWrapper routeKey={key as keyof typeof routesMap} route={route} />
						</Route>
					);
				})
			),
		[systemMode],
	);

	return (
		// TODO: min-w-341.5 temporarily removed
		<Row
			alignItems="stretch"
			justifyContent="start"
			childrenGrow={1}
			childrenShrink={1}
			className="min-w-[800px] print:min-w-auto"
		>
			{platformLayoutOptions.sideBar && user.signedIn && user.id && !needsMfaEnrollment(user) && (
				<AppDrawer key={`${user.id}-side-bar`} />
			)}
			<Switch>{routes}</Switch>
			{/* The kill switch: gated here, so a Sphere AI that is off — or that this environment has no
			    deployment for — mounts nothing at all: no panel, no chat-dock state, no MCP client.
			    See functional-areas/sphere-ai/config.ts for why availability is two conditions. */}
			{sphereAIAvailable() && platformLayoutOptions.sideBar && user.signedIn && user.id && <SphereAIDockPanel />}
		</Row>
	);
};

function PageWrapper<K extends keyof typeof routesMap>({
	routeKey,
	route,
}: {
	routeKey: K;
	route: (typeof routesMap)[K];
}) {
	const user = useUserValue();
	const [platformLayoutOptions, setPlatformLayoutOptions] = useAtom(platformLayoutOptions$);

	useLayoutEffect(() => {
		setPlatformLayoutOptions(maybeCall(route.layoutOptions, { user: user }));
	}, [user, route.layoutOptions, setPlatformLayoutOptions]);

	const history = useHistory();
	const [props, setProps] = useState(() => {
		return typedPropsFromUrl(routeKey, history.location.pathname + history.location.search) ?? ({} as any);
	});

	useEffect(() => {
		const sub = history.listen((location) =>
			setProps(typedPropsFromUrl(routeKey, location.pathname + location.search) ?? ({} as any)),
		);
		return sub;
	}, [history, routeKey]);

	const DynamicallyImportedPage = useMemo(() => {
		return route.component;
	}, [route]);

	// Forced MFA enrollment: a gated user (ROOT/admin without MFA) is blocked from
	// every route except the auth-handshake ones until they enroll — mirroring the
	// backend, which rejects all endpoints but `/mfa/enroll/*`. Rendered in place
	// so no URL can bypass it.
	if (needsMfaEnrollment(user) && !MFA_GATE_EXEMPT_ROUTES.has(routeKey)) {
		// Same full-screen, chromeless envelope as the Login route so the page fills
		// the viewport (it bypasses the matched route's own PageLayout).
		return (
			<PageLayout hideSidebar maxPageHeight="screen" padding={false}>
				<Suspense fallback={<InfiniteLoader />}>
					<MfaEnrollment />
				</Suspense>
			</PageLayout>
		);
	}

	if (route.guard) {
		const guardResult = route.guard({ user: user });
		if (!guardResult.passed) {
			if (guardResult.reason === "requires-login") {
				return <Redirect to={typedUrlForRoute("Login", { from: window.location.pathname + window.location.search })} />;
			}
			return <Redirect to={typedUrlForRoute("Index", {})} />;
		}
	}

	return (
		<PageLayout
			hideSidebar={!platformLayoutOptions.sideBar}
			mode={platformLayoutOptions.mode}
			maxPageHeight={platformLayoutOptions.maxPageHeight}
			padding={platformLayoutOptions.padding}
			minWidth={platformLayoutOptions.minWidth}
			maxWidth={platformLayoutOptions.maxWidth}
		>
			<Suspense fallback={<InfiniteLoader />}>
				<DynamicallyImportedPage {...props} />
			</Suspense>
		</PageLayout>
	);
}

export default PlatformRouter;
